import { ask as askAgent, globalAxios, QFood } from "@q-food/q-food";
import type { AssistantScreenContext } from "~/composables/useAssistantContext";

export interface AssistantStreamHandlers {
	onProgress?: (label: string) => void;
	onToken?: (text: string) => void;
	onError?: (message: string) => void;
}

/** Summary of a saved conversation, as returned by the conversations list/create endpoints. */
export interface ConversationSummary {
	id: string;
	title: string;
	createdAt: string;
	updatedAt: string | null;
}

/** One rendered turn of a stored transcript. */
export interface TranscriptMessage {
	role: "user" | "assistant";
	content: string;
}

/** Full transcript of one conversation. */
export interface ConversationTranscript {
	id: string;
	title: string;
	messages: TranscriptMessage[];
}

/**
 * Talks to the backend data-Q&A agent.
 *
 * - `ask` — one-shot via the generated client (returns the whole answer).
 * - `askStream` — Server-Sent Events via `POST /api/assistant/ask/stream` (stateless).
 * - `askStreamInConversation` — SSE via `POST /api/assistant/conversations/{id}/ask/stream`; the run is
 *   keyed by the conversation so prior turns are remembered (short-term memory) and this turn persists.
 * - conversation CRUD — `getConversations` / `getTranscript` / `createConversation` / `deleteConversation`.
 *
 * Streaming uses raw `fetch` (EventSource can't POST or set headers) and attaches the same `X-Auth-Token`
 * the axios interceptor in `main.ts` adds; CRUD uses the shared `globalAxios` so that auth applies too.
 * Swap the raw calls for the generated client functions once the TypeScript client is regenerated.
 *
 * The optional `context` on the ask calls is the screen the user has open (see `useAssistantContext`);
 * the backend folds it into that turn's prompt so a question with no explicit subject resolves against
 * it. Because the streaming calls are raw `fetch`, they can send it before the client is regenerated —
 * only the generated `ask()` has to wait for the new `AskData`.
 */
export function useAssistant() {
	const conversationsUrl = `${QFood.BASE_URL}/api/assistant/conversations`;

	async function ask(question: string): Promise<string> {
		const { answer } = await askAgent({ question });
		return answer;
	}

	async function askStream(
		question: string,
		handlers: AssistantStreamHandlers,
		context?: AssistantScreenContext,
	): Promise<void> {
		await streamSse(`${QFood.BASE_URL}/api/assistant/ask/stream`, { question, context }, handlers);
	}

	async function askStreamInConversation(
		conversationId: string,
		question: string,
		handlers: AssistantStreamHandlers,
		context?: AssistantScreenContext,
	): Promise<void> {
		await streamSse(`${conversationsUrl}/${conversationId}/ask/stream`, { question, context }, handlers);
	}

	async function getConversations(): Promise<ConversationSummary[]> {
		const { data } = await globalAxios.get(conversationsUrl);
		// Spring returns a Page; the rows are under `content`.
		return (data?.content ?? []) as ConversationSummary[];
	}

	async function getTranscript(id: string): Promise<ConversationTranscript> {
		const { data } = await globalAxios.get(`${conversationsUrl}/${id}`);
		return data as ConversationTranscript;
	}

	async function createConversation(title: string): Promise<ConversationSummary> {
		const { data } = await globalAxios.post(conversationsUrl, { title });
		return data as ConversationSummary;
	}

	async function deleteConversation(id: string): Promise<void> {
		await globalAxios.delete(`${conversationsUrl}/${id}`);
	}

	return {
		ask,
		askStream,
		askStreamInConversation,
		getConversations,
		getTranscript,
		createConversation,
		deleteConversation,
	};
}

/**
 * POST `body` to `url` and dispatch the Server-Sent Events it streams back
 * (`progress` / `token` / `error` — `done` simply ends the stream) to `handlers`.
 */
async function streamSse(url: string, body: unknown, handlers: AssistantStreamHandlers): Promise<void> {
	const token = localStorage.getItem("qfood-session-token");
	const response = await fetch(url, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Accept: "text/event-stream",
			...(token ? { "X-Auth-Token": token } : {}),
		},
		body: JSON.stringify(body),
	});
	if (!response.ok || !response.body) {
		throw new Error(`Assistant stream failed (${response.status})`);
	}

	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let buffer = "";
	for (;;) {
		const { value, done } = await reader.read();
		if (done) break;
		buffer += decoder.decode(value, { stream: true });

		// SSE frames are separated by a blank line; keep the trailing partial frame in the buffer.
		const frames = buffer.split("\n\n");
		buffer = frames.pop() ?? "";
		for (const frame of frames) {
			const data = frame
				.split("\n")
				.filter((line) => line.startsWith("data:"))
				.map((line) => line.slice(5).trim())
				.join("");
			if (!data) continue;

			let payload: { type: string; label?: string; text?: string; message?: string };
			try {
				payload = JSON.parse(data);
			} catch {
				continue;
			}
			if (payload.type === "progress") handlers.onProgress?.(payload.label ?? "");
			else if (payload.type === "token") handlers.onToken?.(payload.text ?? "");
			else if (payload.type === "error") handlers.onError?.(payload.message ?? "Er is iets misgegaan.");
		}
	}
}
